<template>
  <div
    class="row items-center"
    :style="$slots.default ? '' : 'display: inline-flex;'"
  >
    <slot v-if="$slots.default" />
    <template v-if="!props.hideTooltipIcon">
      <div
        class="tooltip-trigger"
        :class="{
          [`re-${props.size}`]: $props.size,
          [computedTriggerColor]: computedTriggerColor,
          exponent: props.isExp,
          smaller: props.isSmaller || props.isExp,
        }"
        @mouseenter="isVisibleRef = true"
        @mouseleave="isVisibleRef = false"
      >
        <div :class="computedIcon ? 'tooltip-trigger--icon-container' : 'tooltip-trigger--text-container'">
          <re-icon
            v-if="computedIcon"
            :name="computedIcon"
            :color="computedIconColor"
          />
          <span v-else>{{ props.triggerText }}</span>
        </div>
      </div>
    </template>
    <q-tooltip
      v-if="!props.hidden"
      v-bind="{ ...tooltipAttrs(props.direction), ...$attrs }"
      v-model="isVisibleRef"
      class="bg-dark text-body text-white text-body1"
      max-width="30em"
      :class="props.tooltipClasses"
    >
      <slot
        v-if="$slots.content"
        name="content"
      />
      <vue-markdown
        v-else-if="props.isMarkdown"
        :source="props.label"
      />
      <template v-else>
        <template v-if="isLabelArray">
          <div
            v-for="text in props.label"
            :key="text"
          >
            {{ text }}
          </div>
        </template>
        <div v-else>
          {{ props.label }}
        </div>
      </template>
    </q-tooltip>
  </div>
</template>
<script setup>
import { computed, ref, watch } from 'vue'
import VueMarkdown from 'vue-markdown-render'

const props = defineProps({
  label: {
    type: [String, Array],
    default: '',
  },
  triggerText: {
    type: [String, Number],
    default: '?',
  },
  triggerColor: {
    type: String,
    default: 'bg-info-light text-info',
  },
  hideTooltipIcon: {
    type: Boolean,
    default: false,
  },
  isVisible: {
    type: Boolean,
    default: false,
  },
  isWarning: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  tooltipClasses: {
    type: Array,
    default: () => [],
  },
  direction: {
    type: String,
    default: 'top',
    validator(value) {
      // The value must match one of these strings
      return ['top', 'right', 'bottom', 'left'].includes(value)
    },
  },
  noPadding: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: undefined,
  },
  iconColor: {
    type: String,
    default: undefined,
  },
  isMarkdown: {
    type: Boolean,
    default: false,
  },
  isExp: {
    type: Boolean,
    default: false,
  },
  isSmaller: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: undefined,
  },
  hidden: {
    type: Boolean,
    default: false,
  },
  isInitiallyVisibleFor: {
    type: Number,
    default: 0,
  },
})

const isVisibleRef = ref(props.isVisible)

watch(
  () => props.isVisible,
  (newValue) => {
    isVisibleRef.value = newValue
  }
)

// Automatically show tooltip for a brief period if isInitiallyVisibleFor is set
if (props.isInitiallyVisibleFor > 0 && !props.isVisible) {
  isVisibleRef.value = true

  setTimeout(() => {
    isVisibleRef.value = false
  }, props.isInitiallyVisibleFor)
}

const isLabelArray = computed(() => Array.isArray(props.label))

const computedIcon = computed(() => {
  if (props.isWarning) return 'mdi-alert'
  if (props.isError) return 'mdi-exclamation-thick'
  return props.icon
})

const computedTriggerColor = computed(() => {
  if (props.isWarning) return 'bg-warning text-white'
  if (props.isError) return 'bg-negative text-white'
  return props.triggerColor
})

const computedIconColor = computed(() => {
  return props.iconColor ?? (props.isWarning || props.isError ? 'white' : 'primary')
})

const tooltipAttrs = (direction) => {
  switch (direction) {
    case 'right':
      return {
        anchor: 'center right',
        self: 'center left',
        transitionShow: 'jump-right',
        transitionHide: 'jump-left',
      }
    case 'left':
      return {
        anchor: 'center left',
        self: 'center right',
        transitionShow: 'jump-left',
        transitionHide: 'jump-right',
      }
    case 'bottom':
      return {
        anchor: 'bottom middle',
        self: 'top middle',
        transitionShow: 'jump-down',
        transitionHide: 'jump-up',
      }
    case 'top':
    default:
      return {
        anchor: 'top middle',
        self: 'bottom middle',
        transitionShow: 'jump-up',
        transitionHide: 'jump-down',
      }
  }
}
</script>
<style lang="scss" scoped>
// Overriding defaults for markdown display within tooltips
:deep(h3) {
  color: $base;
  font-size: 1.3rem;
}

.tooltip-trigger {
  display: inline-block;
  cursor: help;
  text-align: center;
  border-radius: 100%;
  font-weight: 900;
  margin-left: 0.5em;
  position: relative;
  width: 1.15em;
  height: 1.15em;

  &--text-container,
  &--icon-container {
    position: absolute;
    top: 50%;
    left: 50%;
    font-size: 0.8em;
    line-height: 0.9em;
  }

  &--text-container {
    transform: translate(-50%, -50%);
  }

  &--icon-container {
    transform: translate(-50%, -0.55em);

    .q-icon {
      display: block;
      font-size: 1em;
    }
  }

  &.exponent {
    transform: translateY(-0.25em);
    margin-left: 0.15em;
  }

  &.smaller {
    width: 0.75em;
    height: 0.75em;

    .tooltip-trigger--text-container,
    .tooltip-trigger--icon-container {
      font-size: 0.5em;
    }
  }
}
</style>
